// Lets the render reach the screen edges on iOS Safari: the page becomes a
// document one screen tall, scrolled by a runway, with the scene in the scroll
// layer inside #hyper-stage. Why it has to be that shape, and what the page
// half (edge-to-edge.css) defends against: docs/ios-safari-edge-to-edge.md.

import { stageReadout } from './stage-readout.js';

// The top chrome's height cannot be read off the browser, so 62 is measured.
// An error up to SLACK moves the scene without exposing the page background.
const TOP_CHROME = 62;
const SLACK = 24;
const RUNWAY = TOP_CHROME + SLACK;

// Safari 26+ on iPhone is the only browser that floats its chrome over the page;
// iPadOS runs the same engine and does not, so the screen-to-viewport gap cannot
// be the test. `iPhone OS` froze at 18_7, so the version is in `Version/`, and
// iPad answers with a Macintosh UA. Measured: docs/ios-safari-edge-to-edge.md.
const FLOATING_CHROME_SAFARI = 26;

/** Whether the page is staged, from what the browser says right now. */
export function floatsChromeOverThePage( { ua, standalone, screenHeight, innerHeight } ) {
  if ( !/iPhone/.test( ua ) ) { return false; }
  const version = Number( ( ua.match( /Version\/(\d+)/ ) || [] )[ 1 ] );
  if ( !( version >= FLOATING_CHROME_SAFARI ) ) { return false; }
  if ( standalone ) { return false; }
  // Landscape fails from the other side: screen.height stays the portrait
  // height there, so the gap is far wider than any chrome.
  const overlaid = screenHeight - innerHeight;
  return overlaid > 0 && overlaid < 240;
}

/** The three custom properties a staged page publishes, from the screen's
 *  and the window's heights: the document's height, the runway it is
 *  scrolled by, and how far it runs on below the visible strip. */
export function stageMetrics( { screenHeight, innerHeight } ) {
  const height = screenHeight + 2 * SLACK;
  return {
    '--hyper-stage-height': height + 'px',
    '--hyper-runway': RUNWAY + 'px',
    '--hyper-below-viewport': Math.max( 0, height - RUNWAY - innerHeight ) + 'px',
  };
}

/** The document scrolled by the runway: the scene's top under the chrome. */
export const STAGE_RUNWAY = RUNWAY;

export function browserFloatsChromeOverThePage() {
  return floatsChromeOverThePage( {
    ua: navigator.userAgent,
    standalone: window.matchMedia( '(display-mode: standalone)' ).matches,
    screenHeight: window.screen.height,
    innerHeight: window.innerHeight,
  } );
}

// When the answer is asked again after a size change: once the event's own
// burst is over, and twice more as the layout settles. A rotation, the
// address bar sliding, or a page arriving from a crossing reports passing
// sizes on the way, and an answer read off one of them held until the next
// resize — which may not come.
export const RESTAGE_AFTER_MS = [ 50, 300, 1000 ];

// Once per page: more than one of a page's modules may ask (kids2025's tour
// loads both aframe/bootstrap.js and virtualtour.js), and a second set of
// listeners would pin twice.
let asked = false;

// Whether some element from `el` up to (not including) the document scrolls
// along that axis, and has something to scroll.
function aListCanTake( el, vertical ) {
  for ( let node = el; node && node !== document.documentElement && node !== document.body; node = node.parentElement ) {
    if ( !( node instanceof Element ) ) { continue; }
    const style = getComputedStyle( node );
    const overflow = vertical ? style.overflowY : style.overflowX;
    if ( overflow !== 'auto' && overflow !== 'scroll' ) { continue; }
    if ( vertical ? node.scrollHeight > node.clientHeight : node.scrollWidth > node.clientWidth ) {
      return true;
    }
  }
  return false;
}

export function stageTheRenderEdgeToEdge() {
  if ( asked ) { return; }
  // The scene, or a shell's frames (/hypertour/) whose own pages draw it.
  const stage = document.getElementById( 'hyper-stage' );
  const scene = stage && stage.querySelector( 'a-scene' );
  if ( !stage || !( scene || stage.querySelector( 'iframe' ) ) ) { return; }
  asked = true;

  const html = document.documentElement;
  let staged = false;
  const readout = stageReadout( { runway: RUNWAY, slack: SLACK } );

  // The scene's box when it was last sized. A-Frame sizes the canvas once,
  // 100 ms after a resize on iOS (a-scene.js), which can be before a rotation
  // has settled; each restage sizes it again if the box has moved since.
  let sizedAt = '';
  function sceneBox() {
    return scene ? `${ scene.offsetWidth }x${ scene.offsetHeight }` : '';
  }
  function resizeScene() {
    if ( scene && scene.hasLoaded ) { scene.resize(); sizedAt = sceneBox(); }
  }

  function pin() {
    if ( !staged || Math.abs( window.scrollY - RUNWAY ) <= 1 ) { return; }
    window.scrollTo( { top: RUNWAY, behavior: 'instant' } );
  }

  // Furniture on an edge cannot be position:fixed here, so publish where the
  // visible strip sits inside the document.
  function publishMetrics() {
    const metrics = stageMetrics( { screenHeight: window.screen.height, innerHeight: window.innerHeight } );
    for ( const [ name, value ] of Object.entries( metrics ) ) { html.style.setProperty( name, value ); }
  }

  // A rotation changes the answer, so staging is a state, not a decision.
  function restage( reason = 'start' ) {
    const wanted = browserFloatsChromeOverThePage();
    // The head may have staged the page already (stage-early.js): `staged`
    // starts false, so a page staged there is staged here as a change, and
    // one it staged that this does not want is unstaged as one.
    const changed = wanted !== staged || ( !wanted && html.classList.contains( 'hyper-edge-to-edge' ) );
    staged = wanted;
    // Before the class, so no rule it feeds applies without it; and on every
    // pass, because a collapsing address bar moves the strip's bottom.
    if ( staged ) { publishMetrics(); }
    if ( changed ) {
      html.classList.toggle( 'hyper-edge-to-edge', staged );
      // embedded is what sizes the canvas from the stage instead of
      // document.body, and drops the fixed box the bands live behind.
      if ( scene ) { scene.setAttribute( 'embedded', staged ? '' : 'false' ); }
      if ( !staged ) {
        html.style.removeProperty( '--hyper-stage-height' );
        html.style.removeProperty( '--hyper-runway' );
        html.style.removeProperty( '--hyper-below-viewport' );
        window.scrollTo( { top: 0, behavior: 'instant' } );
      }
      resizeScene();
    } else if ( sceneBox() !== sizedAt ) {
      resizeScene();
    }
    // A-Frame's rotation workaround leaves an inline height on <html> until
    // a reload; on the stage the stylesheet outranks it, and it goes here so
    // nothing else reads it either. Unstaged it is A-Frame's to keep.
    if ( staged ) { html.style.removeProperty( 'height' ); }
    pin();
    if ( readout ) { readout.note( reason, staged, changed ); }
  }

  // A drag that no list can take does not scroll the document either. The
  // stylesheet says so with touch-action (edge-to-edge.css), but a touch that
  // starts on fixed furniture is not one WebKit is known to check against the
  // root, and the pin then fights the throw frame by frame. So a touchmove
  // along an axis no element under the finger can scroll is cancelled here;
  // one a list can take — the menu, the cards, the category row sideways — is
  // left to it.
  let startX = 0, startY = 0;
  addEventListener( 'touchstart', ( e ) => {
    const t = e.touches[ 0 ];
    if ( t ) { startX = t.clientX; startY = t.clientY; }
  }, { passive: true } );
  addEventListener( 'touchmove', ( e ) => {
    if ( !staged || e.touches.length !== 1 ) { return; }
    const t = e.touches[ 0 ];
    const vertical = Math.abs( t.clientY - startY ) >= Math.abs( t.clientX - startX );
    if ( !aListCanTake( e.target, vertical ) ) { e.preventDefault(); }
  }, { passive: false } );

  let pinning = false;
  // A scroll listener that scrolls is a loop unless it waits a frame.
  addEventListener( 'scroll', () => {
    if ( pinning ) { return; }
    pinning = true;
    requestAnimationFrame( () => { pin(); pinning = false; } );
  } );
  // The address bar, the keyboard and a rotation all resize under the pin;
  // a page shown from the back-forward cache comes back at whatever size the
  // screen is now.
  let settling = [];
  function restageAsItSettles( e ) {
    const reason = e ? e.type : 'start';
    for ( const t of settling ) { clearTimeout( t ); }
    settling = RESTAGE_AFTER_MS.map( ( ms ) => setTimeout( () => restage( `${ reason }+${ ms }` ), ms ) );
  }
  addEventListener( 'resize', restageAsItSettles );
  addEventListener( 'orientationchange', restageAsItSettles );
  addEventListener( 'pageshow', restageAsItSettles );
  if ( window.visualViewport ) { window.visualViewport.addEventListener( 'resize', restageAsItSettles ); }
  addEventListener( 'load', ( e ) => { restage( 'load' ); restageAsItSettles( e ); } );
  if ( scene ) { scene.addEventListener( 'loaded', resizeScene, { once: true } ); }
  restage();
  restageAsItSettles();
}
